/**
 * Cálculo de aposentadoria - Regras vigentes após EC 103/2019 (Reforma da Previdência).
 * Simplificado - o cálculo real do INSS envolve fator previdenciário, teto e outros detalhes.
 */

const CURRENT_YEAR = new Date().getFullYear();

// Aposentadoria por idade (urbano)
// Regras de idade progressiva pós-reforma:
// - Homem: 65 anos (mantido)
// - Mulher: em 2019 era 60, subiu 6 meses por ano até 62 em 2023
const AGE_URBAN = { male: 65, female: 62 };
const AGE_RURAL = { male: 60, female: 55 };

// Tempo mínimo de contribuição
const MIN_CONTRIB_AGE = 15; // 15 anos para aposentadoria por idade (regra vigente para quem já contribuía)
const MIN_CONTRIB_TIME = { male: 35, female: 30 }; // regra antiga (transição)

export function calcAposentadoria(input) {
  const gender = input.gender || 'male';
  const birthDate = new Date(input.birthDate);
  const startContribDate = new Date(input.startContribDate);
  const monthlyContrib = Number(input.monthlyContrib) || 0;
  const type = input.type || 'idade'; // 'idade' | 'tempo' | 'idade-rural'
  const referenceDate = new Date();

  if (isNaN(birthDate.getTime()) || isNaN(startContribDate.getTime()) || !monthlyContrib) {
    return { error: 'Preencha corretamente todos os campos.' };
  }

  // Age at reference
  const ageMs = referenceDate - birthDate;
  const ageYears = Math.floor(ageMs / (365.25 * 86400000));

  // Contribution years so far
  const contribMs = referenceDate - startContribDate;
  const contribYears = Math.max(0, contribMs / (365.25 * 86400000));

  // Determine required age & contribution based on type
  let requiredAge, requiredContrib, label;
  if (type === 'idade') {
    requiredAge = AGE_URBAN[gender];
    requiredContrib = MIN_CONTRIB_AGE;
    label = gender === 'female' ? 'Aposentadoria por idade (mulher)' : 'Aposentadoria por idade (homem)';
  } else if (type === 'idade-rural') {
    requiredAge = AGE_RURAL[gender];
    requiredContrib = 15;
    label = gender === 'female' ? 'Aposentadoria rural (mulher)' : 'Aposentadoria rural (homem)';
  } else {
    // tempo de contribuição - regra de transição por pontos (idade + contribuição)
    // Pontos em 2025: mulher 92, homem 102 (aumentam 1 ponto por ano até 100/105)
    requiredAge = 0;
    requiredContrib = MIN_CONTRIB_TIME[gender];
    label = gender === 'female' ? 'Aposentadoria por tempo (mulher)' : 'Aposentadoria por tempo (homem)';
  }

  // Points rule (only for tempo)
  const pointsGoal = gender === 'female'
    ? Math.min(100, 86 + (CURRENT_YEAR - 2019))
    : Math.min(105, 96 + (CURRENT_YEAR - 2019));
  const currentPoints = ageYears + contribYears;

  const meetsAge = ageYears >= requiredAge;
  const meetsContrib = contribYears >= requiredContrib;
  const meetsPoints = type === 'tempo' ? currentPoints >= pointsGoal : true;
  const qualifies = meetsAge && meetsContrib && meetsPoints;

  // Missing time to eligibility
  const missingAge = Math.max(0, requiredAge - ageYears);
  const missingContrib = Math.max(0, requiredContrib - contribYears);
  const missingPoints = type === 'tempo' ? Math.max(0, pointsGoal - currentPoints) : 0;

  // Estimated benefit value:
  // Regra pós-reforma: 60% da média salarial + 2% por ano acima do mínimo (15 para mulher, 20 para homem)
  const minForBonus = gender === 'female' ? 15 : 20;
  const contribCap = Math.min(40, contribYears);
  const bonusYears = Math.max(0, contribCap - minForBonus);
  const percent = 60 + bonusYears * 2;
  const benefit = Math.min(monthlyContrib, monthlyContrib * (percent / 100));

  // Minimum wage 2025 = R$ 1518 (aprox) -- INSS mínimo = 1 SM
  const minBenefit = 1518;
  const finalBenefit = Math.max(minBenefit, benefit);
  const cappedByCeiling = 8157.41; // teto INSS 2025 aprox
  const displayBenefit = Math.min(cappedByCeiling, finalBenefit);

  // Projeção de quando poderá se aposentar (se ainda não qualifica)
  let readyDate = null;
  if (!qualifies) {
    const yearsUntil = Math.max(missingAge, missingContrib, missingPoints / 2);
    const target = new Date(referenceDate);
    target.setFullYear(target.getFullYear() + Math.ceil(yearsUntil));
    readyDate = target;
  }

  return {
    qualifies,
    label,
    ageYears,
    contribYears: Math.round(contribYears * 10) / 10,
    requiredAge,
    requiredContrib,
    missingAge,
    missingContrib: Math.round(missingContrib * 10) / 10,
    missingPoints,
    currentPoints: Math.round(currentPoints * 10) / 10,
    pointsGoal,
    percent,
    benefit: displayBenefit,
    minBenefit,
    ceiling: cappedByCeiling,
    readyDate,
    type,
  };
}

export function formatBRL(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}
